import type { ReactNode } from "react";
import type {
  ClaimStatus,
  HeldPositionDto,
  HeldPositionRouting,
  HeldReasonCode,
} from "@/api/commissions";

/**
 * Why a held BOOK share is held, in the surface's own words.
 *
 * 🔴 A `Record<HeldReasonCode, …>` AND NOT A LOOKUP WITH A FALLBACK
 * SENTENCE, for the reason `HELD_COPY` below is one: a reason added to the
 * server vocabulary without copy must be a TYPECHECK ERROR here, not an
 * invented sentence under a code nobody wrote words for. The server's side is a
 * list of `const string` that no compiler checks against this, so the other
 * half of the guarantee is `heldReasonCodes.parity.test.ts`, which reads the C#
 * and fails when a member is added there and not here.
 *
 * 🔴 THE `fix` SENTENCE IS THE POINT, and the two groups need different
 * ones. A STAMPED reason is a fact about the closing: nothing clears it on its
 * own and an ADMIN correction to the loan's attribution is the only thing that
 * does. A RE-CHECKED reason is a fact about today: it clears itself when the
 * condition does, so telling a reviewer to go and correct the attribution would
 * send them to the wrong screen entirely.
 *
 * The `label` is what the All Claims table shows under "Unassigned — held",
 * where a full sentence would wreck the column; `cause` and `fix` are the claim
 * detail's note. All three are the CLIENT'S words — the code is server
 * vocabulary and is never rendered raw.
 */
type HeldReasonCopy = { label: string; cause: string; fix: string };

/** Every stamped reason has the same remedy, and it is the only remedy: the
 *  attribution is frozen at closing and nothing else rewrites it. Written once
 *  so seven copies cannot drift into seven slightly different instructions. */
const CORRECT_THE_ATTRIBUTION =
  "An ADMIN correction to this loan's commission attribution is what releases it, and it re-routes every share on the loan that is still suggested.";

export const HELD_REASON_COPY: Record<HeldReasonCode, HeldReasonCopy> = {
  // ── Stamped at closing ────────────────────────────────────────────────
  "attribution not stamped": {
    label: "Attribution not stamped",
    cause:
      "This loan has no commission attribution at all — either the stamp failed when it closed, or it closed before the attribution existed.",
    fix: CORRECT_THE_ATTRIBUTION,
  },
  "multiple buyer leads": {
    label: "Competing buyer leads",
    cause:
      "Two or more buyer leads name this loan's borrower, so which consultant worked it cannot be decided from the record.",
    // The one stamped reason with a cheaper first move than a correction, and
    // naming it matters: merging the duplicates fixes the cause rather than
    // overriding it, and the detail beside this carries the competing ids.
    fix: `Merging the duplicate buyer leads is the root fix. ${CORRECT_THE_ATTRIBUTION}`,
  },
  "no specialist at closing": {
    label: "No consultant at closing",
    cause:
      "This loan's buyer lead had nobody assigned to it when the loan closed, so the share is owed and has no payee. That is different from having no buyer lead at all, which owes nobody and writes no claim.",
    fix: CORRECT_THE_ATTRIBUTION,
  },
  "specialist on no team": {
    label: "Consultant on no team",
    cause:
      "The consultant who worked this buyer was on no team when the buyer was assigned to her, so there is no book for the manager's share to sit in.",
    fix: CORRECT_THE_ATTRIBUTION,
  },
  "team has no single f1 manager": {
    label: "Team has no single F1 manager",
    cause:
      "That team had no single manager holding F1 when the buyer was assigned — none, or more than one. Both stop the share resolving and they need opposite corrections.",
    fix: CORRECT_THE_ATTRIBUTION,
  },
  "no broker liaison": {
    label: "No broker liaison on the deal",
    cause:
      "This loan has no buyer lead, so it sits in a broker liaison's book — and its deal carries no liaison assignment.",
    fix: CORRECT_THE_ATTRIBUTION,
  },
  "no team history": {
    label: "No team history that far back",
    cause:
      "The backfill could not reconstruct the team or its manager as they stood when the buyer was assigned, because the audit record does not reach back that far. It is held rather than stamped with today's team, which would name the wrong person confidently.",
    fix: CORRECT_THE_ATTRIBUTION,
  },

  // ── Re-checked on every routing pass ──────────────────────────────────
  "payee deactivated": {
    label: "Payee deactivated",
    cause:
      "This share resolves to somebody whose account is deactivated and whose commission arrangement carries no end date, so whether the share is still hers is undecided.",
    // 🔴 NOT the attribution. The attribution is correct here — it named the
    // right person — and a correction would be the wrong screen.
    fix: "Setting the end date on her commission arrangement releases it, either to her (the income predates it) or not at all (it does not). Nothing about this loan's attribution needs changing.",
  },
  "month already accepted": {
    label: "Month already accepted",
    cause:
      "This share resolved after the named person's claim for this pool and month had already been accepted. It is never added to an accepted claim: that figure is one somebody has agreed to.",
    fix: "A manual adjustment on the accepted claim is the only way to pay it. Correcting the attribution will not move it.",
  },
};

/**
 * A HELD claim's claimant, in the All Claims table and the claim detail header.
 *
 * 🔴 The state this exists for used to render as NOTHING. Both surfaces wrote
 * `claimantDisplayName ?? claimantUserId`, and for a held claim both operands
 * are empty: `ClaimantUserId` is the empty string (non-nullable, empty-string
 * sentinel — never null, so a `?? ` guard cannot catch it) and the name lookup
 * drops empty ids, so `??` falls from null to `""` and React renders nothing.
 * A blank cell reads as a rendering fault or a claim nobody has got to yet; it
 * is neither. It is money allocated to a position nobody currently fills.
 *
 * 🔴 HELD IS TWO CONDITIONS, NOT ONE: `Suggested` AND an empty claimant. The
 * status half is not decoration. `CommissionAutoSuggestService` withdraws a
 * superseded roll-up WITHOUT clearing its claimant, so an empty claimant
 * outlives the hold and a terminal row keeps it for life. Testing emptiness
 * alone labels a withdrawn claim "held" — on the detail page, directly above
 * "Claim withdrawn — terminal state." — and the All Claims filter now offers
 * Withdrawn, so a reviewer lands on exactly those rows deliberately. This
 * mirrors the server, which gates its own diagnosis on both conditions.
 *
 * Otherwise mirrors `ClaimLoan`, which draws the same distinction one column
 * over: a value absent for a REASON gets named, not em-dashed.
 */
export function ClaimClaimant({
  status,
  claimantUserId,
  claimantDisplayName,
  heldReason,
}: {
  status: ClaimStatus;
  claimantUserId: string;
  claimantDisplayName?: string | null;
  /** The short WHY, under the label. "Unassigned — held" on its own is the
   *  thing that tells a reviewer nothing, and All Claims is the surface they
   *  work the held queue from. Absent on a roll-up (whose held summary rides
   *  the description) and on a non-book position (whose hold is about a role
   *  count, and is explained by <HeldPositionNote/> on the detail).
   *
   *  The claim DETAIL deliberately does not pass it: there this renders inline
   *  inside a sentence that continues "· Created …", where a second line would
   *  break the run, and the full note lower down carries the same reason with
   *  its cause and its fix. */
  heldReason?: HeldReasonCode | null;
}) {
  if (claimantUserId === "") {
    // A code from a newer server than this bundle has no copy here. Say the
    // claim is held and stop, rather than rendering server vocabulary raw.
    const reason = heldReason ? HELD_REASON_COPY[heldReason] : undefined;
    return status === "Suggested" ? (
      <span className="font-display text-label font-semibold uppercase tracking-label text-status-warning">
        Unassigned — held
        {reason && (
          <span className="block font-sans text-caption font-normal normal-case tracking-normal text-gray-mid">
            {reason.label}
          </span>
        )}
      </span>
    ) : (
      // Never held, but never owned either — a superseded roll-up, say. The
      // em dash is ClaimLoan's own answer for "there is nothing here".
      <span>—</span>
    );
  }
  return <span>{claimantDisplayName ?? claimantUserId}</span>;
}

/**
 * What is holding a held claim, and what would release it.
 *
 * 🔴 EVERY SENTENCE IS BRANCHED ON `routing`, `isRollup` AND — on the firm-wide
 * tier — `firmWideHolderCount`, and that is the correction that matters. The
 * count reaches the client precisely because zero holders and two-or-more
 * holders both resolve to nobody while needing OPPOSITE remedies; a sentence
 * blind to it gives the 2+ cell a wait-for-a-grant instruction, and a grant is
 * the one thing that cannot help there. An earlier draft branched the cause on the tier
 * and the resolution on roll-up-ness ALONE, which produced a confidently false
 * sentence on the most common cell of all: a per-payment claim on a FIRM-WIDE
 * role was told "once the position is assigned, this claim routes to that
 * holder". It does not. TWO methods unhold a per-payment claim, and a role
 * grant is a caller of neither. `RouteHeldClaimsForLoanAsync` has four call
 * sites in `src/` — `LoanAssignmentService` and both `DealAssignmentService`s
 * (the deal pills, and the reassign endpoints, the only writers of the C1,
 * B1 and A1 positions), all
 * ASSIGNMENT paths, plus `LoanCommissionAttributionService`, which routes a
 * loan's held claims the moment its attribution is stamped (income from a
 * closing can be recognised before the stamp exists). `RerouteBookClaimsForLoanAsync`
 * is the other, reached by the ADMIN attribution correction and the backfill;
 * it is wider, because it also moves claims that are already routed. Granting
 * a firm-wide role code writes a `UserRoleCodes` row and calls nothing. Pool A
 * seeds firm-wide allocations (C0, E4, E3-E5), so every loan boarding mints
 * per-payment claims in exactly that cell.
 *
 * ⚠️ This said "the only thing … exactly two callers, both ASSIGNMENT
 * paths", which was true until the stamp and the correction landed. The
 * ARGUMENT is unchanged — no role grant reaches any of them — but a census in
 * prose is a fact about disk, so re-derive it rather than trusting this line.
 *
 * 🔴 THE COPY IS A `Record<HeldPositionRouting, …>` AND NOT AN `if`/`else`
 * CHAIN, so a tier added without copy is a TYPECHECK ERROR. It is not a
 * stylistic preference: the `else` arm used to say "`${roleCode}` is held on
 * the deal rather than the loan", and when the BOOK tier arrived (F1 Buyer
 * Manager, F2 Buyer Consultant) that arm caught it and said something false
 * twice over — a book share is not held on the deal, and assigning a position
 * does not release it. Each tier also returns its cause AND its resolution
 * together, because the defect above was exactly cause and resolution branching
 * on different dimensions.
 *
 * The other verified constraints:
 *  - It says "holds the role", never "active user". The resolver queries
 *    `UserRoleCodes` with no `IsActive` filter and no join to `Users`, so
 *    deactivating a second holder does NOT release the claim.
 *  - It does not claim a position is "unassigned". The diagnosis reads
 *    `UserRoleCodes` and never `LoanAssignments`, so that would be asserted
 *    from no read at all.
 *  - The header avoids "no one fills this position", which is false at two or
 *    more holders — re-collapsing on screen the very distinction the holder
 *    count was carried out to the client to preserve.
 */
type HeldCopy = { header: string; cause: string; resolution: string };
type HeldContext = { roleCode: string; holders: number; isRollup: boolean };

/** The header for every tier whose hold really is about the POSITION. `Book` is
 *  the exception and says so in its own entry. */
const POSITION_HEADER = "Held — this position resolves to nobody";

/** `LoanPill` and `DealAssignment` differ in WHERE the position is filled and
 *  not in what releases the claim, so the release sentence is written once.
 *  Two copies would be two places for it to drift. */
function assignmentResolution(isRollup: boolean): string {
  return isRollup
    ? "This monthly roll-up carries no single loan, so it cannot be routed by hand: the hourly sweep redrafts it as the position is filled. Filling it on some loans and not others leaves a smaller held claim covering the rest."
    : "Assigning the position on this loan routes this claim to that holder.";
}

const HELD_COPY: Record<HeldPositionRouting, (ctx: HeldContext) => HeldCopy> = {
  LoanPill: ({ roleCode, isRollup }) => ({
    header: POSITION_HEADER,
    cause: `${roleCode} is held per loan, on the loan's assignment pills.`,
    resolution: assignmentResolution(isRollup),
  }),

  DealAssignment: ({ roleCode, isRollup }) => ({
    header: POSITION_HEADER,
    cause: `${roleCode} is held on the deal rather than the loan.`,
    resolution: assignmentResolution(isRollup),
  }),

  // 🔴 BOTH FIRM-WIDE ARMS BRANCH ON THE COUNT, and that is the whole reason
  // the count is carried to the client. The resolver is
  // `firmWideMap[role] = ids.Count == 1 ? ids[0] : null`, so ZERO holders and
  // TWO OR MORE holders both resolve to nobody — but they need OPPOSITE
  // actions. At zero you grant the role; at two or more granting it again
  // makes it worse, and the fix is to take it off the extras.
  //
  // An earlier version computed `cause` from the count and `resolution` from
  // the tier alone, so the 2+ cell was told "once one person holds the role…"
  // — a wait-for-a-grant instruction on the one cell where a grant cannot
  // help. The per-payment arm was worse than unhelpful: it promised that an
  // assignment "re-runs the router, which then picks the position up", but the
  // router resolves through that same `ids.Count == 1` rule and picks up
  // nothing while a second holder remains.
  FirmWide: ({ roleCode, holders, isRollup }) => {
    const tooManyHolders = holders >= 2;

    const cause =
      holders === 0
        ? `No one holds ${roleCode}. This position resolves to a single firm-wide holder of the role code, so it stays unresolved until exactly one person carries it.`
        : holders === 1
          ? `${roleCode} resolves to a single holder now, so the position itself is no longer the obstacle.`
          : `${holders} people hold ${roleCode}. This position resolves only to a single firm-wide holder, so it stays unresolved until exactly one carries the role code.`;

    const resolution = isRollup
      ? tooManyHolders
        ? `This monthly roll-up carries no single loan, so it cannot be routed by hand: the hourly sweep redrafts the month only once exactly one person carries ${roleCode}. Take the role code off the others in Admin → Users — granting it to anyone else leaves it held.`
        : "This monthly roll-up carries no single loan, so it cannot be routed by hand: once one person holds the role, the hourly sweep redrafts the month against them."
      : tooManyHolders
        ? // 🔴 The cell the earlier draft got wrong. Say plainly that the
          // obvious action is not the trigger, because a reviewer who grants the
          // role and walks away gets no error and no signal.
          `Granting the role code does not route this claim on its own, and an assignment will not either while ${holders} people carry ${roleCode} — the router resolves a firm-wide position to a single holder only. Take the role code off the others in Admin → Users, then assign the position on this loan.`
        : "Granting the role code does not route this claim on its own — only an assignment on this loan re-runs the router, which then picks the position up.";

    return { header: POSITION_HEADER, cause, resolution };
  },

  // 🔴 THE BOOK TIER SAYS NOTHING ABOUT THE POSITION, and that is the
  // difference from every arm above. F1 is paid on her team's book and F2 on
  // the buyers she personally worked, resolved per loan from the
  // `LoanCommissionAttribution` frozen at closing — so several people hold F1
  // at once BY DESIGN and no count means anything here. Granting the role code,
  // taking it off somebody, and assigning a pill are all irrelevant; only an
  // ADMIN correction to that loan's attribution moves it.
  //
  // The header is the one place this differs visibly, and it has to: "this
  // position resolves to nobody" would send a reviewer to Admin → Users to
  // look at a role grant that is not the problem.
  //
  // TODO(WS4): the stamped held REASON ("no specialist at closing", "team has
  // no single f1 manager", …) is what actually names the fix, and it reaches
  // the wire in WS4. Until it does, this sentence deliberately points at the
  // attribution rather than guessing which of the reasons applies.
  Book: ({ roleCode, isRollup }) => ({
    header: "Held — this loan's commission attribution does not name a payee",
    cause: `${roleCode} is paid per loan, from the commission attribution frozen on the loan at closing — not from who holds the role code today. Several people carry ${roleCode} at once by design, so granting or removing it changes nothing here.`,
    resolution: isRollup
      ? "This monthly roll-up carries no single loan: each held loan is released by an ADMIN correction to its own commission attribution, and the hourly sweep redrafts the month once they are."
      : "An ADMIN correction to this loan's commission attribution is what releases it, and it re-routes every share on the loan that is still suggested.",
  }),
};

export function HeldPositionNote({
  held,
  isRollup,
  heldReason,
  heldReasonDetail,
  action,
}: {
  held: HeldPositionDto;
  isRollup: boolean;
  /** Why THIS loan's share is held, where the tier above says only how the
   *  position is routed. Book positions only. */
  heldReason?: HeldReasonCode | null;
  /** The specifics — the date behind "no team history", the competing lead ids
   *  behind "multiple buyer leads". Rendered beside the code, never instead. */
  heldReasonDetail?: string | null;
  /**
   * The remedy's own control, when the caller has one and the caller's reader
   * may use it.
   *
   * 🔴 A SLOT AND NOT A BUTTON THIS COMPONENT DECIDES TO RENDER. Whether a
   * remedy is reachable is a question about the READER (a permission) and about
   * the SURFACE (a roll-up has no single loan to correct, and its per-loan
   * links live on the claim's own funding events), and this component is handed
   * neither. Rendering a control here on the strength of `routing === "Book"`
   * alone would put a "Correct attribution" button in front of every reviewer
   * on every held book claim, including the roll-ups it cannot act on and the
   * readers the endpoint would 403.
   *
   * Absent by default, so every existing mount is unchanged.
   */
  action?: ReactNode;
}) {
  const { roleCode, routing, firmWideHolderCount: holders } = held;

  // A routing value the client does not know about can only come from a server
  // newer than this bundle. Fall back to the position header and say nothing
  // about a cause or a cure rather than inventing one — the `Book` defect this
  // Record exists to prevent was exactly an invented sentence.
  const copy = HELD_COPY[routing]?.({ roleCode, holders, isRollup });

  // 🔴 THE REASON'S `fix` REPLACES THE TIER'S RESOLUTION, it does not sit
  // beside it. The tier resolution for `Book` is "correct the attribution",
  // which is right for the seven STAMPED reasons and WRONG for both RE-CHECKED
  // ones: "payee deactivated" is released by setting an end date on a
  // commission arrangement and "month already accepted" by an adjustment on the
  // accepted claim. Printing both would put a reviewer on the wrong screen half
  // the time and look authoritative doing it. The tier CAUSE stays, because it
  // explains why the position works this way at all.
  const reason = heldReason ? HELD_REASON_COPY[heldReason] : undefined;

  return (
    <div className="mt-space-3 p-space-3 border border-status-warning rounded-md bg-status-warning/5 text-small">
      <span className="font-display font-semibold uppercase text-label tracking-label text-status-warning">
        {copy?.header ?? POSITION_HEADER}
      </span>
      {copy && (
        <div className="text-onyx mt-space-1">
          {copy.cause} {reason ? `${reason.cause} ${reason.fix}` : copy.resolution}
        </div>
      )}
      {reason && heldReasonDetail && (
        <div className="text-gray-mid mt-space-1">{heldReasonDetail}</div>
      )}
      {action && <div className="mt-space-2">{action}</div>}
    </div>
  );
}
